<template>
    <asli-form @failed="handleError" scroll-to-error class="mx-auto max-w-[-moz-available] w-full">
        <div class="rounded-lg py-2 px-1">
            <h2 v-if="steps[currentStep].title" class="text-xl md:text-2xl font-bold mb-4">
                {{ steps[currentStep].title }}
            </h2>
            <AccountDetails
                v-if="currentStep === DirectDebitStep.ACCOUNT_DETAILS"
                :directDebitDetails="directDebitDetails"
                @updateDirectDebitDetails="updateDirectDebitDetails"
                :policyName="policyName"
                :errorMessage="errorMessage"
                @validated="handleValidated"
                @updateErrorMessage="updateErrorMessage"
            />
            <BankDetails v-if="currentStep === DirectDebitStep.BANK_DETAILS" @prev="changeBankDetails" />
            <TermsAndConditions
                v-if="currentStep === DirectDebitStep.TERMS_AND_CONDITIONS"
                @agreedConfirmed="handleAgreedConfirmed"
                :confirmedAuthorization="confirmedAuthorization"
            />
            <Deposit v-if="currentStep === DirectDebitStep.DEPOSIT" />
            <div v-if="currentStep !== DirectDebitStep.DEPOSIT" class="mt-6">
                <asli-button
                    id="step_action_button"
                    name="step_action_button"
                    color="secondary"
                    type="submit"
                    class="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 disabled:bg-gray-300 text-sm md:text-lg"
                    :disabled="!canProceed"
                    @click="handleStepAction"
                    :loading="isLoading"
                >
                    {{ currentButtonText }}
                </asli-button>
            </div>
            <div class="mt-4 flex justify-center">
                <div v-for="(step, index) in steps" :key="step.id" class="flex items-center">
                    <div
                        class="w-3 h-3 rounded-full mx-2"
                        :class="{
                            'bg-yellow': index === currentStep,
                            'bg-grey-50': index < currentStep || index > currentStep
                        }"
                    />
                </div>
            </div>
        </div>
    </asli-form>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from "vue";
import { useProposalStore } from "@/stores/risk/risk";
import { DirectDebit } from "@/stores/risk/types/types";
import { PersonalDetails } from "@/stores/risk/types/drivers/driver-types";
import { AsliButton, AsliForm } from "@mbs-npm/anpost-ui";
import AccountDetails from "./AccountDetails.vue";
import BankDetails from "./BankDetails.vue";
import Deposit from "./Deposit.vue";
import TermsAndConditions from "./TermsAndConditions.vue";
import { DirectDebitStep } from "@/static/constants";

const props = defineProps({
    currentStep: {
        type: Number,
        default: 0
    }
});
const proposalStore = useProposalStore();
const step = ref(props.currentStep);
const errorMessage = ref<string | null>("");
const agreedConfirmed = ref(false);
const confirmedAuthorization = ref(false);
const isLoading = ref(false);

const emit = defineEmits(["update:currentStep"]);

function handleAgreedConfirmed(val: boolean) {
    agreedConfirmed.value = val;
}

const validationState = reactive({
    sort_code: false,
    account_number: false,
    iban: false
});

const personalDetails = reactive<Partial<PersonalDetails>>({
    first_name: proposalStore.proposal.risk?.proposer?.personal_details?.first_name,
    surname: proposalStore.proposal.risk?.proposer?.personal_details?.surname
});

const policyName = computed(() => `${personalDetails.first_name} ${personalDetails.surname}`);

const directDebitDetails = reactive<DirectDebit>({
    account_name: proposalStore.proposal.direct_debit?.account_name || "",
    sort_code: proposalStore.proposal.direct_debit?.sort_code || null,
    account_number: proposalStore.proposal.direct_debit?.account_number || null,
    iban: proposalStore.proposal.direct_debit?.iban || null,
    branch_name: proposalStore.proposal.direct_debit?.branch_name || null,
    bank_name: proposalStore.proposal.direct_debit?.bank_name || null,
    bic_code: proposalStore.proposal.direct_debit?.bic_code || null,
    address_line_1: proposalStore.proposal.direct_debit?.address_line_1 || null,
    address_line_2: proposalStore.proposal.direct_debit?.address_line_2 || null,
    address_line_3: proposalStore.proposal.direct_debit?.address_line_3 || null,
    address_line_4: proposalStore.proposal.direct_debit?.address_line_4 || null,
    eircode: proposalStore.proposal.direct_debit?.eircode || null,
    ecad_id: proposalStore.proposal.direct_debit?.ecad_id || null
});

const steps = [
    {
        id: "account-details",
        title: "Bank Account Details",
        component: AccountDetails,
        buttonText: "Continue"
    },
    {
        id: "bank-details",
        title: "Check the Details",
        component: BankDetails,
        buttonText: "Confirm Details"
    },
    {
        id: "terms",
        title: "Terms and Conditions",
        component: TermsAndConditions,
        buttonText: "Agree and Setup Direct Debit"
    },
    {
        id: "deposit",
        component: Deposit,
        buttonText: ""
    }
];

const currentButtonText = computed(() => steps[props.currentStep].buttonText);

const canProceed = computed(() => {
    switch (props.currentStep) {
        case 0:
            return (
                (validationState.iban || (validationState.sort_code && validationState.account_number)) &&
                !errorMessage.value
            );
        case 1:
            return true;
        case 2:
            return agreedConfirmed.value;
        case 3:
            return true;
        default:
            return false;
    }
});

watch(
    () => props.currentStep,
    newVal => {
        step.value = newVal;
    }
);

async function handleStepAction() {
    isLoading.value = true;
    try {
        if (props.currentStep === DirectDebitStep.ACCOUNT_DETAILS) {
            try {
                await submitDetails();
            } catch {
                handleError();
            }
        } else if (props.currentStep !== 3) {
            step.value++;
            emit("update:currentStep", step.value);
        }
    } catch (e) {
        handleError();
    } finally {
        isLoading.value = false;
    }
}

async function submitDetails() {
    try {
        proposalStore.patchDirectDebitDetails(directDebitDetails);
        await proposalStore.UpdatePaymentDetails({
            is_pay_monthly: true,
            sort_code: directDebitDetails.sort_code?.replaceAll("-", "")
        });
        step.value++;
        emit("update:currentStep", step.value);
    } catch (e) {
        handleError();
    } finally {
        isLoading.value = false;
    }
}

function changeBankDetails() {
    step.value--;
    emit("update:currentStep", step.value);
}

function updateDirectDebitDetails(value: DirectDebit) {
    Object.assign(directDebitDetails, value);
}

function handleError() {
    errorMessage.value = directDebitDetails.iban
        ? "Please check the value you have entered. Your IBAN should be 22 characters using letters and numbers only. We can only accept IBANs from Irish bank accounts."
        : "Please check the bank account sort code and account number you have entered. We can only accept Irish bank account details.";

    setTimeout(() => {
        const errorElement = document.getElementById("error-message");
        if (errorElement) {
            errorElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, 0);
}

function handleValidated(field: keyof DirectDebit, valid: boolean) {
    validationState[field] = valid;
    errorMessage.value = "";
}

function updateErrorMessage(newMessage: string | null) {
    errorMessage.value = newMessage;
}
</script>
